import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  CircularProgress,
  MenuItem,
  Stack,
  Typography,
  Menu,
  IconButton,
  ListItemIcon,
  SvgIcon,
  Dialog,
  DialogContent, useMediaQuery, Drawer
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAuth } from 'hooks/useAuthContext';
import { useNavigate } from 'react-router-dom';
import { deleteResume, createDuplicateResume, updateResumeUrl } from 'api/users';
import { useSnackbar } from 'components/snackbar';
import RemoveCard from './RemoveCard';
import { setApplication } from 'api/application';
import { useTheme } from '@mui/material/styles';
import { createPDF } from 'utils/createPDF';
import PDFViewer from 'components/PDFViewer';
import ResumeDownload from 'components/job-application/ResumeDownload';
import { getTimeDifferenceString, toTitleCase, downloadBlobFile, downloadWordDoc } from 'utils/Utils';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import LeaveWithoutCreate from './LeaveWithoutCreate';
import ResumeDownloaded from 'components/job-application/ResumeDownloaded';
import UploadResumeNavigation from './UploadResumeNavigation';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';


/**
 * MyResumeDetails is a React component responsible for rendering the details of a user's resume.
 * It handles various functionalities such as uploading, downloading, duplicating, and deleting resumes.
 * It also manages the state of the resume's PDF document and loading status.
 *
 * Props:
 * - data: Object containing information about the resume.
 * - totalResume: Total number of resumes.
 * - onDataChange: Function to call when there is a change in the resume data.
 * - onClose: Function to call when closing the component.
 * - jobData: Object containing job-related data, used for external applications.
 * - redirectedToExternalJob: Function to handle redirection for external job applications.
 * - setPreviewLoader: Function to set the loading status of the preview.
 *
 * The component relies on several hooks and utilities for authentication, navigation, and notifications.
 * It also provides UI elements and interactions for different mobile and desktop views.
 */

const MyResumeDetails = ({ data, totalResume, onDataChange, onClose, jobData, redirectedToExternalJob, setPreviewLoader, tjResumeCount }) => {
  const fileInputRef = useRef(null);
  const { user, setJobApply } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resumeId = data?._id || '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  // const [openRemoveCard, setOpenRemoveCard] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const titleData = data?.link ? 'Uploaded Resume' : data?.title || '';

  // const [openDownloadResumeDialog, setOpenDownloadResumeDialog] = useState(false);

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const isRemoveCardOpen = dialogIds.includes('removeCard');
  const isDownloadResumeDialogOpen = dialogIds.includes('downloadResumeDialog');
  const isOverlayDialogOpen = dialogIds.includes('overlayDialog');
  const isDownloadResumeBottomSheetOpen = dialogIds.includes('downloadResumeBottomSheet');


  const [downloadResumeMenu, setDownloadResumeMenu] = useState(null);
  const openDownResumeMenu = Boolean(downloadResumeMenu);

  const [resumeActionAnchorEl, setResumeActionAnchorEl] = useState(null);
  const openActionMenu = Boolean(resumeActionAnchorEl);

  const [isDuplicating, setIsDuplicating] = useState(false);
  // const [showOverlayDialog, setShowOverlayDialog] = useState(false);
  const [showContinueDialog, setShowContinueDialog] = useState(false);

  useEffect(() => {
    if (!isDownloadResumeBottomSheetOpen && showContinueDialog && jobData) {
      const timer = setTimeout(() => {
        handleOpenDownloadResumeDialog();
        setShowContinueDialog(false);
      }, 500); // or 100ms if needed

      return () => clearTimeout(timer);
    }
  }, [isDownloadResumeBottomSheetOpen, showContinueDialog]);

  useEffect(() => {
    setLoading(true);
    if (debounceTimeout) clearTimeout(debounceTimeout);
    const newTimeout = setTimeout(() => {
      generatePdfBuffer(data);
    }, 500);

    setDebounceTimeout(newTimeout);
  }, [data]);

  const handleOpenDownloadResumeDialog = () => openDialog('downloadResumeDialog');
  const handleCloseDownloadResumeDialog = () => closeDialog('downloadResumeDialog');

  /**
   * Generates a PDF buffer from the provided resume data.
   * This function is used to generate a PDF document of the user's resume.
   * It uses the createPDF utility function to generate a PDF buffer from
   * the provided data, and then converts the buffer to a blob and sets it
   * to the component's state as pdfDoc.
   * If there is an error, it logs the error and sets the loading state to false.
   * @param {Object} data - Resume data to generate the PDF buffer from.
   */
  const generatePdfBuffer = async (data) => {
    try {
      const buffer = await createPDF({
        profile: user,
        educationData: data?.educations ?? [],
        workExperienceData: data?.work_history ?? [],
        certificateData: data?.certificates ?? [],
        customSection: data?.custom_section ?? [],
        summaryData: data?.summary,
        financeTechologiesData: data?.finance_technologies ?? []
      });
      const blob = new Blob([buffer], { type: 'application/pdf' });
      setPdfDoc(blob);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false)
    }
  };
  /**
   * Closes the resume details drawer by calling the onClose function.
   * Ensures that the drawer interface is properly closed and any necessary
   * cleanup actions are performed when the drawer is closed.
   */

  const handleCloseDrawer = () => {
    onClose();
  };
  /**
   * Navigates to the create-update-resume page with the current resume data
   * and the total number of resumes the user has. This function is used to
   * provide the user with a convenient way to edit their resume from the
   * resume details page.
   */
  const handleNavigation = () => {
    navigate('/create-update-resume', { state: { data, totalResume, jobData, isExternalJob: true } });
  };

  /**
   * Triggers the file input click event to upload a resume.
   * This function is responsible for initiating the file selection dialog
   * for uploading a resume. It uses the `fileInputRef` reference to click
   * on the hidden file input element, allowing the user to select a resume
   * file from their device.
   */

  const handleUploadResume = () => {
    // navigate('/create-update-resume', { state: { data, totalResume, uploadResume: true } });
    fileInputRef.current.click()
  }

  /**
   * Deletes a resume by calling the deleteResume function with the current
   * resume id. If the response is successful, it calls onDataChange to
   * refresh the resume data and closes the action menu. If the response
   * fails, it displays an error snackbar. Also, it removes the current
   * resume card from the list and if it's on mobile, it closes the drawer.
   */
  const handleDelete = async () => {
    const response = await deleteResume({ id: resumeId });
    if (response?.success) {
      setResumeActionAnchorEl(null);
      onDataChange();
    } else {
      enqueueSnackbar(response?.message || ' unable to delete', { variant: 'error' });
    }
    handleRemoveCard();
    if (isMobile) onClose();
  };

  /**
   * Duplicates a resume by calling the createDuplicateResume function with
   * the current resume id. If the response is successful, it navigates to
   * the create-update-resume page with the duplicated resume data and the
   * total number of resumes the user has. If the response fails, it displays
   * an error snackbar. It also sets isDuplicating to false when the function
   * is done.
   */
  const handleDuplicateResume = async () => {
    setIsDuplicating(true);
    try {
      setPreviewLoader(true);
      const response = await createDuplicateResume({ resumeId });
      if (response?.success && response.data) {
        setResumeActionAnchorEl(null);
        const resumeData = response.data;
        onDataChange(data?._id);
        navigate('/create-update-resume', { state: { data: resumeData, totalResume: totalResume + 1, isDuplicate: true, jobData, isExternalJob: true } });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Failed to fetch data. Please try again later.', { variant: 'error' });
    } finally {
      setIsDuplicating(false);
      setPreviewLoader(false);
    }
  };

  /**
   * Handles the delete section click event.
   * It stops the event propagation, sets the resume action menu anchor element to null,
   * and opens the remove card dialog.
   * @param {Event} event - The event object that triggered the delete section click event.
   */
  const handleDeleteSection = async (event) => {
    event.stopPropagation();
    setResumeActionAnchorEl(null);
    // setOpenRemoveCard(true);
    openDialog('removeCard');
  };

  /**
   * Handles the removal of a resume card by closing the remove card dialog.
   * When the function is called, it sets the openRemoveCard state to false, effectively closing the dialog.
   * It does not modify the itemId state variable, even though it is commented out in the code.
   * This function is used in the ResumeDetails component to close the remove card dialog when the user
   * clicks on the cancel button or outside of the dialog.
   */
  const handleRemoveCard = () => {
    // setOpenRemoveCard(false);
    closeDialog('removeCard');
  };

  const [withoutUpdateDialog, setLeaveWihoutUpdatingDialog] = React.useState(false);
  /**
   * Opens the LeaveWithoutUpdatingDialog or triggers a process based on certain conditions.
   * This function is responsible for handling the logic to open a dialog
   * if there are unsaved changes or initiate a specific process by calling
   * the handleProcessClicked function.
   */

  const handleOpenLeaveWihoutUpdatingDialog = () => {
    handleProcessClicked();
  };
  /**
   * Closes the LeaveWithoutUpdatingDialog by setting the
   * withoutUpdateDialog state to false.
   */
  const handleCloseLeaveWihoutUpdatingDialog = () => {
    setLeaveWihoutUpdatingDialog(false);
  };
  /**
   * Handles the continuation of an external job application.
   * Opens the external job application link in a new window and initiates
   * the process to mark the job as in-progress.
   *
   * Extracts necessary details such as candidate information, job data,
   * and matching skills from the `jobData` object. Depending on the current
   * tab context, it determines the correct external link to open using `window.open`.
   * Then, it calls `handleJobInprogress` to update the status of the job application.
   */

  const handleContinueExternalApplication = () => {
    const { candidateDetails, matchingSkills, job, tab } = jobData;
    if (jobData) {
      const externalLink = tab === '2' ? job?.external_link : job?.jobDetails?.external_link;
      window.open(externalLink);
      handleJobInprogress(candidateDetails, matchingSkills, job, tab);
    }
  };

  /**
   * Handles the continuation of an external job application.
   * Opens the external job application link in a new window and initiates
   * the process to mark the job as in-progress.
   *
   * Extracts necessary details such as candidate information, job data,
   * and matching skills from the `jobData` object. Depending on the current
   * tab context, it determines the correct external link to open using `window.open`.
   * Then, it calls `handleJobInprogress` to update the status of the job application.
   */
  const handleJobInprogress = async (candidateDetails, matchingSkills, job, tab) => {
    try {
      setIsSubmitting(true);
      if (candidateDetails && matchingSkills && job) {
        const jobId = tab === '2' ? job?._id : job?.job_id;
        const jobDetails = tab === '2' ? job : job?.jobDetails;
        const response = await setApplication({ candidateDetails, matchingSkills, jobId, jobDetails, status: 'application pending', tj_score: job?.tj_score });
        if (response?.success && response?.data != null) {
          const company = response?.data?.job_id?.company;
          const title = response?.data?.job_id?.title;
          enqueueSnackbar(`Your application to ${toTitleCase(company)} for the ${title} role is in progress`, { variant: 'info' });
          const application = response?.data;
          application.jobDetails = application?.job_id;
          application.job_id = application?.job_id?._id;
          redirectedToExternalJob(job, tab);
          // setOpenDownloadResumeDialog(false);
          handleCloseDownloadResumeDialog();
        } else {
          enqueueSnackbar(response?.message, { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Failed to submit the application. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Downloads the resume PDF by calling the handleDownload function with the
   * resume link and user data, or the downloadResume function if the link is
   * not available. If successful, it closes the download resume bottom sheet,
   * sets the download resume menu to null, and opens the download resume dialog
   * if jobData is provided. If an error occurs, it logs the error.
   */
  const handleDownloadResumePdf = async () => {
    try {
      if (data?.link) {
        await handleDownload(data?.link, user);
      } else {
        await downloadResume();
      }
      closeDownloadResumeBottomSheet();
      setDownloadResumeMenu(null);

      if (jobData) {
        setShowContinueDialog(true);
        // setOpenDownloadResumeDialog(true);
        // setTimeout(() => {
        //   handleOpenDownloadResumeDialog();
        // }, 5000);
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  // const [downloadResumeBottomSheet, setDownloadResumeBottomSheet] = useState(false);
  const openDownloadResumeBottomSheet = () => openDialog('downloadResumeBottomSheet');
  const closeDownloadResumeBottomSheet = () => closeDialog('downloadResumeBottomSheet');

  /**
   * Downloads the resume as a Word document.
   * If the `word_link` is available in the data, it uses `downloadDocx` to download the document.
   * Otherwise, it constructs the resume data from user details, education, work history,
   * certifications, custom sections, and competencies, and uses `downloadWordDoc` to create and download it.
   * If an error occurs during the download process, it logs the error to the console.
   * Regardless of success or failure, it closes the download resume bottom sheet.
   * If `jobData` is available, it opens the download resume dialog; otherwise, it resets the download resume menu.
   */

  const handleDownloadResumeDocs = async () => {
    try {
      if (data?.word_link) {
        await downloadDocx(data?.word_link);
      } else {
        await downloadWordDoc([
          user.photo || user.profile_pic, user, {
            user: user,
            educations: data?.educations ?? [],
            experiences: data?.work_history ?? [],
            certifications: data?.certificates ?? [],
            customSection: data?.custom_section ?? [],
            professional: data?.summary,
            technicalCompetencies: data?.finance_technologies ?? []
          }]);
      }
    } catch (error) {
      console.error("Error downloading Word document:", error);
    } finally {
      closeDownloadResumeBottomSheet();
      if (jobData) {
        // setOpenDownloadResumeDialog(true);
        // handleOpenDownloadResumeDialog();
        setShowContinueDialog(true);
      } else {
        setDownloadResumeMenu(null);
      }
    }
  };

  /**
   * Downloads the resume as a PDF file.
   * If the necessary data is available, it formats the file name using the user's name and the resume title
   * and triggers the download using the `downloadBlobFile` utility. Logs an error if required data is missing.
   */

  const downloadResume = () => {
    if (!pdfDoc || !user?.name || !data?.title) {
      console.error("Missing required data for file name.");
      return;
    }
    const formattedFileName = `${user.name.trim()} (${data.title.trim()}).pdf`;

    downloadBlobFile(pdfDoc, formattedFileName);
  };

  /**
   * Downloads a file from the given URL and saves it as a PDF with the given user's name.
   * If the file URL or user is missing, it logs an error message to the console.
   * If the download fails, it logs an error message to the console.
   * @param {string} fileUrl - The URL of the file to be downloaded.
   * @param {object} user - The user object containing the name to be used for the file name.
   */
  const handleDownload = async (fileUrl, user) => {
    if (!fileUrl || !user?.name) {
      console.error("File URL is missing");
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_END_POINT}${fileUrl}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const formattedFileName = `${user.name.trim()} (Uploaded Resume).pdf`;
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", formattedFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  /**
   * Handles file change event for the file input element.
   * Checks if the selected file is valid (not empty, less than 2MB, and of allowed type).
   * If the file is valid, it uploads the file to the server and updates the local resume data.
   * If the upload fails, it logs an error to the console and displays an error message to the user.
   * Finally, it opens the overlay dialog to display the updated resume data.
   */
  const handleFileChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      enqueueSnackbar("No file selected", { variant: "error" });
      return;
    };
    const file = e.target.files[0];
    if (file.size > 2 * 1024 * 1024) {
      enqueueSnackbar("File size must be less than 2 MB. Please upload a smaller file", { variant: "error" });
      return;
    }
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar("Invalid file type. Only PDF, DOC, and DOCX files are allowed", { variant: "error" });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await updateResumeUrl(formData);
      if (response?.success && response?.data) {
        onDataChange();
      } else {
        enqueueSnackbar("Failed to update resume", { variant: "error" });
        return;
      }
    }
    catch (error) {
      console.error("Error uploading file:", error);
      enqueueSnackbar("Error uploading file. Please try again.", { variant: "error" });
      setLoading(false);
    } finally {
      openDialog('overlayDialog');
      // setShowOverlayDialog(true);
    }
  }

  /**
   * Handles the click event on the Process button in the overlay dialog.
   * Calls the handleDuplicateResume function to duplicate the resume.
   */
  const handleProcessClicked = () => {
    handleDuplicateResume();
  };
  /**
   * Cancels the overlay dialog and hides it by setting the showOverlayDialog
   * state to false.
   */
  const handleCancel = () => {
    closeDialog('overlayDialog');
    // setShowOverlayDialog(false);
  };
  /**
   * Navigates to the "create-update-resume" page with the given data,
   * total number of resumes, and a flag indicating that a resume is being uploaded.
   */

  const handleProceed = () => {
    navigate('/create-update-resume', { state: { data, totalResume, uploadResume: true, jobData, isExternalJob: true } });
  };

  /**
   * Downloads the given fileUrl as a Word document with the given filename.
   * @param {string} fileUrl - The URL of the file to download.
   * @throws {Error} If there is an error downloading the file.
   */

  const downloadDocx = async (fileUrl) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_END_POINT}${fileUrl}`);

      if (!response.ok) throw new Error('Failed to download');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const extension = fileUrl.endsWith('.docx') ? 'docx' : 'doc';

      const link = document.createElement('a');
      link.href = url;
      link.download = `${user.name.trim()} (${data.title.trim()}).${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };
  const handleDoItLater = () => {
    const { candidateDetails, matchingSkills, job, tab } = jobData;
    // setOpenDownloadResumeDialog(false);
    handleCloseDownloadResumeDialog();
    redirectedToExternalJob(job, tab, true);
  };

  return (
    <Box className="my-resume-details">
      <input type="file"
        accept=".pdf, .doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <Box className="my-resume-details-header">
        <Box className="header-top" sx={{ borderBottom: "1px solid #dae1e8" }}>
          <Stack direction="row" justifyContent={'space-between'} spacing={2}>
            <Stack direction="row" alignItems={'center'} spacing={2}>
              <IconButton aria-label="delete" sx={{ display: { xs: 'block', md: 'none', lg: 'none' } }} onClick={handleCloseDrawer}>
                <img src="/assets/back-arrow-white.png" alt="Back" width="20px" height="20px" loading="lazy" />
              </IconButton>
              <Box>
                <Typography className="resume-title">{data?.link ? 'Uploaded Resume' : (data?.title || 'Master Resume')}</Typography>
                <Typography className="last-updated-date">{getTimeDifferenceString(data?.last_updated_at || data?.updatedAt, "Updated") || 'N/A'}</Typography>
              </Box>
            </Stack>
            <Box>
              <Stack direction="row" alignItems={'center'} spacing={3}>
                <Button variant="outlined" size="large" className="save-btn" onClick={data?.link ? handleUploadResume : handleNavigation} sx={{ display: { xs: 'none!important', md: 'flex!important' } }}>
                  {data?.link ? 'Replace' : 'Update'}
                </Button>
                <Button
                  variant="contained"
                  className="apply-now-btn"
                  aria-controls={openDownResumeMenu ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openDownResumeMenu ? 'true' : undefined}
                  onClick={(event) => {
                    event.stopPropagation();
                    setDownloadResumeMenu(event.currentTarget);
                  }}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{ display: { xs: 'none!important', md: 'flex!important' } }}
                >
                  Download
                </Button>
                <Menu
                  id="basic-menu"
                  className="download-resume-menu"
                  anchorEl={downloadResumeMenu}
                  open={openDownResumeMenu}
                  onClose={() => setDownloadResumeMenu(null)}
                  sx={{ marginTop: 1 }}
                >
                  <MenuItem onClick={handleDownloadResumePdf} disabled={loading}>Download PDF{loading && <CircularProgress size={20} style={{ marginLeft: 10 }} />}</MenuItem>
                  <MenuItem disabled={loading || (!!data?.link && !data?.word_link)} onClick={handleDownloadResumeDocs}>Download DocX (Word){loading && <CircularProgress size={20} style={{ marginLeft: 10 }} />}</MenuItem>
                </Menu>

                <IconButton
                  aria-label="more"
                  aria-controls={openActionMenu ? 'resume-action-menu' : undefined}
                  aria-expanded={openActionMenu ? 'true' : undefined}
                  aria-haspopup="true"
                  onClick={(event) => setResumeActionAnchorEl(event.currentTarget)}
                  className="resume-action-menu-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="4" height="20" viewBox="0 0 4 20" fill="none">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M2 4C0.895431 4 5.79551e-07 3.10457 6.27833e-07 2C6.76115e-07 0.89543 0.895431 -1.35705e-07 2 -8.74228e-08C3.10457 -3.91405e-08 4 0.89543 4 2C4 3.10457 3.10457 4 2 4ZM2 12C0.895431 12 7.06698e-07 11.1045 7.54981e-07 9.99997C8.03263e-07 8.8954 0.895431 7.99997 2 7.99997C3.10457 7.99997 4 8.8954 4 9.99997C4 11.1045 3.10457 12 2 12ZM8.82124e-07 18C8.33842e-07 19.1046 0.895431 20 2 20C3.10457 20 4 19.1046 4 18C4 16.8955 3.10457 16 2 16C0.895431 16 9.30406e-07 16.8955 8.82124e-07 18Z"
                      fill="#1575C6"
                    />
                  </svg>
                </IconButton>

                <Menu
                  id="resume-action-menu"
                  className="resume-action-menu"
                  anchorEl={resumeActionAnchorEl}
                  open={openActionMenu}
                  onClose={() => setResumeActionAnchorEl(null)}
                  sx={{ marginTop: 2 }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={() => { handleOpenLeaveWihoutUpdatingDialog(); setResumeActionAnchorEl(null); }} disabled={isDuplicating || tjResumeCount === 4 || !!data?.link}>
                    <ListItemIcon>
                      <SvgIcon sx={{ width: '14px', height: '14px' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8.42243 1.56899H7.39569V1.22675C7.394 0.660398 6.93531 0.201702 6.36896 0.200012H1.57752C1.01117 0.201702 0.552471 0.660398 0.550781 1.22675V7.40428C0.552471 7.97064 1.01117 8.42933 1.57752 8.43102H2.60426V8.77327C2.60595 9.33961 3.06464 9.79832 3.63099 9.80001H8.42243C8.98879 9.79832 9.44748 9.33961 9.44917 8.77327V2.59573C9.44748 2.02938 8.98879 1.57069 8.42243 1.56899ZM2.87223 1.91124H2.26201C2.073 1.91124 1.91977 2.06447 1.91977 2.25348C1.91977 2.4425 2.073 2.59573 2.26201 2.59573H2.60426V3.28022H2.26201C2.073 3.28022 1.91977 3.43345 1.91977 3.62247C1.91977 3.81149 2.073 3.96472 2.26201 3.96472H2.60426V4.64921H2.26201C2.073 4.64921 1.91977 4.80244 1.91977 4.99145C1.91977 5.18047 2.073 5.3337 2.26201 5.3337H2.60426V6.01819H2.26201C2.073 6.01819 1.91977 6.17142 1.91977 6.36043C1.91977 6.54945 2.073 6.70268 2.26201 6.70268H2.60426V7.74653H1.57752C1.3885 7.74653 1.23527 7.5933 1.23527 7.40428V1.22675C1.23527 1.03773 1.3885 0.884505 1.57752 0.884505H6.36896C6.55798 0.884505 6.71121 1.03773 6.71121 1.22675V1.56899H3.63099C3.34094 1.57011 3.06505 1.69455 2.87223 1.91124ZM8.76468 8.77327C8.76468 8.96228 8.61145 9.11551 8.42243 9.11551H3.63099C3.44197 9.11551 3.28874 8.96228 3.28874 8.77327V2.59573C3.28874 2.40671 3.44197 2.25348 3.63099 2.25348H8.42243C8.61145 2.25348 8.76468 2.40671 8.76468 2.59573V8.77327ZM4.31562 4.64952H7.73808C7.92709 4.64952 8.08032 4.80275 8.08032 4.99176C8.08032 5.18078 7.92709 5.33401 7.73808 5.33401H4.31562C4.1266 5.33401 3.97337 5.18078 3.97337 4.99176C3.97337 4.80275 4.1266 4.64952 4.31562 4.64952ZM7.73785 6.01779H4.31539C4.12637 6.01779 3.97314 6.17101 3.97314 6.36003C3.97314 6.54905 4.12637 6.70228 4.31539 6.70228H7.73785C7.92686 6.70228 8.08009 6.54905 8.08009 6.36003C8.08009 6.17101 7.92686 6.01779 7.73785 6.01779ZM7.73808 7.38711H4.31562C4.1266 7.38711 3.97337 7.54034 3.97337 7.72936C3.97337 7.91837 4.1266 8.0716 4.31562 8.0716H7.73808C7.92709 8.0716 8.08032 7.91837 8.08032 7.72936C8.08032 7.54034 7.92709 7.38711 7.73808 7.38711ZM4.31562 3.28049H7.73808C7.92709 3.28049 8.08032 3.43372 8.08032 3.62274C8.08032 3.81175 7.92709 3.96498 7.73808 3.96498H4.31562C4.1266 3.96498 3.97337 3.81175 3.97337 3.62274C3.97337 3.43372 4.1266 3.28049 4.31562 3.28049Z"
                            fill="#1F9CD7"
                          />
                          <path
                            d="M7.39569 1.56899H7.29569V1.66899H7.39569V1.56899ZM8.42243 1.56899L8.42273 1.46899H8.42243V1.56899ZM7.39569 1.22675H7.49569L7.49569 1.22645L7.39569 1.22675ZM6.36896 0.200012L6.36926 0.100012H6.36896V0.200012ZM1.57752 0.200012V0.100012L1.57722 0.100013L1.57752 0.200012ZM0.550781 1.22675L0.450781 1.22645V1.22675H0.550781ZM0.550781 7.40428H0.450781L0.450782 7.40458L0.550781 7.40428ZM1.57752 8.43102L1.57722 8.53102H1.57752V8.43102ZM2.60426 8.43102H2.70426V8.33102H2.60426V8.43102ZM2.60426 8.77327H2.50426L2.50426 8.77357L2.60426 8.77327ZM3.63099 9.80001L3.63069 9.90001H3.63099V9.80001ZM8.42243 9.80001V9.90001L8.42273 9.90001L8.42243 9.80001ZM9.44917 8.77327L9.54917 8.77357V8.77327H9.44917ZM9.44917 2.59573H9.54917L9.54917 2.59543L9.44917 2.59573ZM2.87223 1.91124V2.01124H2.91711L2.94694 1.97771L2.87223 1.91124ZM2.60426 2.59573H2.70426V2.49573H2.60426V2.59573ZM2.60426 3.28022V3.38022H2.70426V3.28022H2.60426ZM2.60426 3.96472H2.70426V3.86472H2.60426V3.96472ZM2.60426 4.64921V4.74921H2.70426V4.64921H2.60426ZM2.60426 5.3337H2.70426V5.2337H2.60426V5.3337ZM2.60426 6.01819V6.11819H2.70426V6.01819H2.60426ZM2.60426 6.70268H2.70426V6.60268H2.60426V6.70268ZM2.60426 7.74653V7.84653H2.70426V7.74653H2.60426ZM6.71121 1.56899V1.66899H6.81121V1.56899H6.71121ZM3.63099 1.56899V1.46899L3.63061 1.46899L3.63099 1.56899ZM7.39569 1.66899H8.42243V1.46899H7.39569V1.66899ZM7.29569 1.22675V1.56899H7.49569V1.22675H7.29569ZM6.36866 0.300012C6.88001 0.301538 7.29417 0.715691 7.29569 1.22705L7.49569 1.22645C7.49384 0.605104 6.9906 0.101867 6.36926 0.100013L6.36866 0.300012ZM1.57752 0.300012H6.36896V0.100012H1.57752V0.300012ZM0.650781 1.22705C0.652307 0.715691 1.06646 0.301538 1.57782 0.300012L1.57722 0.100013C0.955873 0.101867 0.452636 0.605104 0.450782 1.22645L0.650781 1.22705ZM0.650781 7.40428V1.22675H0.450781V7.40428H0.650781ZM1.57782 8.33102C1.06646 8.3295 0.652307 7.91534 0.650781 7.40398L0.450782 7.40458C0.452636 8.02593 0.955873 8.52917 1.57722 8.53102L1.57782 8.33102ZM2.60426 8.33102H1.57752V8.53102H2.60426V8.33102ZM2.70426 8.77327V8.43102H2.50426V8.77327H2.70426ZM3.63129 9.70001C3.11994 9.69848 2.70578 9.28432 2.70426 8.77297L2.50426 8.77357C2.50611 9.39491 3.00935 9.89815 3.63069 9.90001L3.63129 9.70001ZM8.42243 9.70001H3.63099V9.90001H8.42243V9.70001ZM9.34917 8.77297C9.34765 9.28432 8.93349 9.69848 8.42213 9.70001L8.42273 9.90001C9.04408 9.89815 9.54732 9.39491 9.54917 8.77357L9.34917 8.77297ZM9.34917 2.59573V8.77327H9.54917V2.59573H9.34917ZM8.42213 1.66899C8.93349 1.67052 9.34765 2.08468 9.34917 2.59603L9.54917 2.59543C9.54732 1.97409 9.04408 1.47085 8.42273 1.46899L8.42213 1.66899ZM2.26201 2.01124H2.87223V1.81124H2.26201V2.01124ZM2.01977 2.25348C2.01977 2.11969 2.12822 2.01124 2.26201 2.01124V1.81124C2.01777 1.81124 1.81977 2.00924 1.81977 2.25348H2.01977ZM2.26201 2.49573C2.12822 2.49573 2.01977 2.38727 2.01977 2.25348H1.81977C1.81977 2.49773 2.01777 2.69573 2.26201 2.69573V2.49573ZM2.60426 2.49573H2.26201V2.69573H2.60426V2.49573ZM2.70426 3.28022V2.59573H2.50426V3.28022H2.70426ZM2.26201 3.38022H2.60426V3.18022H2.26201V3.38022ZM2.01977 3.62247C2.01977 3.48868 2.12822 3.38022 2.26201 3.38022V3.18022C2.01777 3.18022 1.81977 3.37822 1.81977 3.62247H2.01977ZM2.26201 3.86472C2.12822 3.86472 2.01977 3.75626 2.01977 3.62247H1.81977C1.81977 3.86671 2.01777 4.06471 2.26201 4.06471V3.86472ZM2.60426 3.86472H2.26201V4.06471H2.60426V3.86472ZM2.70426 4.64921V3.96472H2.50426V4.64921H2.70426ZM2.26201 4.74921H2.60426V4.54921H2.26201V4.74921ZM2.01977 4.99145C2.01977 4.85767 2.12822 4.74921 2.26201 4.74921V4.54921C2.01777 4.54921 1.81977 4.74721 1.81977 4.99145H2.01977ZM2.26201 5.2337C2.12822 5.2337 2.01977 5.12524 2.01977 4.99145H1.81977C1.81977 5.2357 2.01777 5.4337 2.26201 5.4337V5.2337ZM2.60426 5.2337H2.26201V5.4337H2.60426V5.2337ZM2.70426 6.01819V5.3337H2.50426V6.01819H2.70426ZM2.26201 6.11819H2.60426V5.91819H2.26201V6.11819ZM2.01977 6.36043C2.01977 6.22664 2.12822 6.11819 2.26201 6.11819V5.91819C2.01777 5.91819 1.81977 6.11619 1.81977 6.36043H2.01977ZM2.26201 6.60268C2.12822 6.60268 2.01977 6.49422 2.01977 6.36043H1.81977C1.81977 6.60468 2.01777 6.80268 2.26201 6.80268V6.60268ZM2.60426 6.60268H2.26201V6.80268H2.60426V6.60268ZM2.70426 7.74653V6.70268H2.50426V7.74653H2.70426ZM1.57752 7.84653H2.60426V7.64653H1.57752V7.84653ZM1.13527 7.40428C1.13527 7.64853 1.33327 7.84653 1.57752 7.84653V7.64653C1.44373 7.64653 1.33527 7.53807 1.33527 7.40428H1.13527ZM1.13527 1.22675V7.40428H1.33527V1.22675H1.13527ZM1.57752 0.784505C1.33328 0.784505 1.13527 0.982497 1.13527 1.22675H1.33527C1.33527 1.09296 1.44373 0.984505 1.57752 0.984505V0.784505ZM6.36896 0.784505H1.57752V0.984505H6.36896V0.784505ZM6.81121 1.22675C6.81121 0.982498 6.61321 0.784505 6.36896 0.784505V0.984505C6.50275 0.984505 6.61121 1.09296 6.61121 1.22675H6.81121ZM6.81121 1.56899V1.22675H6.61121V1.56899H6.81121ZM3.63099 1.66899H6.71121V1.46899H3.63099V1.66899ZM2.94694 1.97771C3.12087 1.78225 3.36973 1.67 3.63138 1.66899L3.63061 1.46899C3.31214 1.47022 3.00923 1.60685 2.79753 1.84476L2.94694 1.97771ZM8.42243 9.21551C8.66668 9.21551 8.86468 9.01751 8.86468 8.77327H8.66468C8.66468 8.90706 8.55622 9.01551 8.42243 9.01551V9.21551ZM3.63099 9.21551H8.42243V9.01551H3.63099V9.21551ZM3.18874 8.77327C3.18874 9.01751 3.38675 9.21551 3.63099 9.21551V9.01551C3.4972 9.01551 3.38874 8.90706 3.38874 8.77327H3.18874ZM3.18874 2.59573V8.77327H3.38874V2.59573H3.18874ZM3.63099 2.15348C3.38675 2.15348 3.18874 2.35148 3.18874 2.59573H3.38874C3.38874 2.46194 3.4972 2.35348 3.63099 2.35348V2.15348ZM8.42243 2.15348H3.63099V2.35348H8.42243V2.15348ZM8.86468 2.59573C8.86468 2.35148 8.66668 2.15348 8.42243 2.15348V2.35348C8.55622 2.35348 8.66468 2.46194 8.66468 2.59573H8.86468ZM8.86468 8.77327V2.59573H8.66468V8.77327H8.86468ZM7.73808 4.54952H4.31562V4.74952H7.73808V4.54952ZM8.18032 4.99176C8.18032 4.74752 7.98232 4.54952 7.73808 4.54952V4.74952C7.87187 4.74952 7.98032 4.85798 7.98032 4.99176H8.18032ZM7.73808 5.43401C7.98232 5.43401 8.18032 5.23601 8.18032 4.99176H7.98032C7.98032 5.12555 7.87187 5.23401 7.73808 5.23401V5.43401ZM4.31562 5.43401H7.73808V5.23401H4.31562V5.43401ZM3.87337 4.99176C3.87337 5.23601 4.07138 5.43401 4.31562 5.43401V5.23401C4.18183 5.23401 4.07337 5.12555 4.07337 4.99176H3.87337ZM4.31562 4.54952C4.07138 4.54952 3.87337 4.74752 3.87337 4.99176H4.07337C4.07337 4.85798 4.18183 4.74952 4.31562 4.74952V4.54952ZM4.31539 6.11779H7.73785V5.91779H4.31539V6.11779ZM4.07314 6.36003C4.07314 6.22624 4.1816 6.11779 4.31539 6.11779V5.91779C4.07115 5.91779 3.87314 6.11579 3.87314 6.36003H4.07314ZM4.31539 6.60228C4.1816 6.60228 4.07314 6.49382 4.07314 6.36003H3.87314C3.87314 6.60428 4.07115 6.80228 4.31539 6.80228V6.60228ZM7.73785 6.60228H4.31539V6.80228H7.73785V6.60228ZM7.98009 6.36003C7.98009 6.49382 7.87164 6.60228 7.73785 6.60228V6.80228C7.98209 6.80228 8.18009 6.60428 8.18009 6.36003H7.98009ZM7.73785 6.11779C7.87164 6.11779 7.98009 6.22624 7.98009 6.36003H8.18009C8.18009 6.11579 7.98209 5.91779 7.73785 5.91779V6.11779ZM4.31562 7.48711H7.73808V7.28711H4.31562V7.48711ZM4.07337 7.72936C4.07337 7.59557 4.18183 7.48711 4.31562 7.48711V7.28711C4.07138 7.28711 3.87337 7.48511 3.87337 7.72936H4.07337ZM4.31562 7.9716C4.18183 7.9716 4.07337 7.86314 4.07337 7.72936H3.87337C3.87337 7.9736 4.07138 8.1716 4.31562 8.1716V7.9716ZM7.73808 7.9716H4.31562V8.1716H7.73808V7.9716ZM7.98032 7.72936C7.98032 7.86314 7.87187 7.9716 7.73808 7.9716V8.1716C7.98232 8.1716 8.18032 7.9736 8.18032 7.72936H7.98032ZM7.73808 7.48711C7.87187 7.48711 7.98032 7.59557 7.98032 7.72936H8.18032C8.18032 7.48511 7.98232 7.28711 7.73808 7.28711V7.48711ZM7.73808 3.18049H4.31562V3.38049H7.73808V3.18049ZM8.18032 3.62274C8.18032 3.37849 7.98232 3.18049 7.73808 3.18049V3.38049C7.87187 3.38049 7.98032 3.48895 7.98032 3.62274H8.18032ZM7.73808 4.06498C7.98232 4.06498 8.18032 3.86698 8.18032 3.62274H7.98032C7.98032 3.75653 7.87187 3.86498 7.73808 3.86498V4.06498ZM4.31562 4.06498H7.73808V3.86498H4.31562V4.06498ZM3.87337 3.62274C3.87337 3.86698 4.07138 4.06498 4.31562 4.06498V3.86498C4.18183 3.86498 4.07337 3.75653 4.07337 3.62274H3.87337ZM4.31562 3.18049C4.07138 3.18049 3.87337 3.37849 3.87337 3.62274H4.07337C4.07337 3.48895 4.18183 3.38049 4.31562 3.38049V3.18049Z"
                            fill="#D4EEF9"
                          />
                        </svg>
                      </SvgIcon>
                    </ListItemIcon>
                    Duplicate
                  </MenuItem>
                  <Divider />
                  <MenuItem disabled={totalResume === 1 || (data?._id !== data?.original_id ? false : true)} onClick={handleDeleteSection}>
                    <ListItemIcon>
                      <SvgIcon sx={{ width: '14px', height: '14px' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8.54063 3.4344L8.05823 8.97641C8.01783 9.44201 7.62783 9.80001 7.16023 9.80001H2.72221C2.24781 9.80001 1.855 9.43282 1.82301 8.95922L1.4478 3.43442C1.289 3.43442 1.15821 3.38362 1.0514 3.27681C0.954203 3.17961 0.893799 3.04561 0.893799 2.89801V2.30041C0.893799 2.00522 1.1346 1.76401 1.4302 1.76401H2.96016V0.976411C2.96016 0.548808 3.30937 0.200012 3.73656 0.200012H6.32893C6.75653 0.200012 7.10532 0.549217 7.10532 0.976411H7.10452V1.76401H8.56977C8.86497 1.76401 9.10617 2.00482 9.10617 2.30041V2.89801C9.10617 3.19321 8.86537 3.43442 8.56977 3.43442L8.54063 3.4344ZM6.46098 4.24081V5.9172C6.46098 6.0136 6.53938 6.092 6.63578 6.092C6.73218 6.092 6.81058 6.0136 6.81058 5.9172V4.24081C6.81058 4.14441 6.73217 4.06601 6.63578 4.06601C6.53898 4.06601 6.46098 4.14441 6.46098 4.24081ZM6.46098 6.70763V8.38402C6.46098 8.48042 6.53938 8.55882 6.63578 8.55882C6.73218 8.55882 6.81058 8.48042 6.81058 8.38402V6.70763C6.81058 6.61123 6.73217 6.53283 6.63578 6.53283C6.53898 6.53283 6.46098 6.61083 6.46098 6.70763ZM5.37062 4.24081V8.38402C5.37062 8.48042 5.44902 8.55882 5.54542 8.55882C5.64182 8.55882 5.72022 8.48042 5.72022 8.38402V4.24081C5.72022 4.14441 5.64182 4.06601 5.54542 4.06601C5.44902 4.06601 5.37062 4.14441 5.37062 4.24081ZM4.28027 4.24081V5.9172C4.28027 6.0136 4.35867 6.092 4.45506 6.092C4.55146 6.092 4.62986 6.0136 4.62986 5.9172V4.24081C4.62986 4.14441 4.55146 4.06601 4.45506 4.06601C4.35866 4.06601 4.28027 4.14441 4.28027 4.24081ZM4.28027 6.70763V8.38402C4.28027 8.48042 4.35867 8.55882 4.45506 8.55882C4.55146 8.55882 4.62986 8.48042 4.62986 8.38402V6.70763C4.62986 6.61123 4.55146 6.53283 4.45506 6.53283C4.35866 6.53283 4.28027 6.61083 4.28027 6.70763ZM3.19022 4.24081V8.38402C3.19022 8.48042 3.26862 8.55882 3.36501 8.55882C3.46141 8.55882 3.53981 8.48042 3.53981 8.38402L3.53941 4.24081C3.53941 4.14441 3.46101 4.06601 3.36461 4.06601C3.26822 4.06601 3.19022 4.14441 3.19022 4.24081ZM1.79737 3.4344L2.17096 8.93606C2.19057 9.22686 2.43057 9.45046 2.72216 9.45046H7.16019C7.44899 9.45046 7.68499 9.23446 7.71019 8.94646L8.18979 3.43446L1.79737 3.4344ZM3.30982 1.76436H3.68262L3.68222 1.21996C3.68222 1.00996 3.85342 0.838754 4.06342 0.838754H6.00227C6.21227 0.838754 6.38347 1.00996 6.38347 1.21996V1.76396H6.75707V0.976359H6.75627C6.75627 0.741955 6.56387 0.549156 6.32907 0.549156L3.7367 0.549556C3.5023 0.549556 3.3095 0.741956 3.3095 0.976759L3.30982 1.76436ZM4.03182 1.76436H6.03426V1.21996C6.03426 1.20276 6.01946 1.18836 6.00266 1.18836H4.06381C4.04661 1.18836 4.03221 1.20316 4.03221 1.21996L4.03182 1.76436ZM8.5702 2.11357H1.43055C1.32815 2.11357 1.24375 2.19797 1.24375 2.30036V2.89796C1.24375 3.00036 1.32815 3.08476 1.43055 3.08476H8.5702C8.6726 3.08476 8.757 3.00036 8.757 2.89796V2.30036C8.757 2.19796 8.6726 2.11357 8.5702 2.11357Z"
                            fill="#1F9CD7"
                          />
                        </svg>
                      </SvgIcon>
                    </ListItemIcon>
                    Delete
                  </MenuItem>
                </Menu>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>
      <Box className="my-resume-overview" >
        <Box className="pdf-container" sx={{
          "& .react-pdf__Page__canvas": {
            maxWidth: "100% !important",
            height: "auto !important",
          },
          "& .react-pdf__Document": {
            width: "fit-content !important",
          },
          "& .pdf-page-container": {
            paddingBottom: `${isMobile || isTablet ? '0px !important' : '20px !important'}`,
          },
        }} style={{ position: 'relative', height: '75vh', width: '100%', overflowY: 'auto', padding: isMobile || isTablet ? '0px' : '20px 0', backgroundColor: loading ? '' : '#0808086b', display: 'flex', justifyContent: 'center', }}>
          {loading ?
            <div className="loading-spinner">
              <CircularProgress />
            </div> : (data?.link ? <>
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />
              <Viewer
                fileUrl={process.env.REACT_APP_END_POINT + data?.link}
                {...(isMobile && { defaultScale: SpecialZoomLevel.PageWidth })}
              />

            </> :
              <PDFViewer pdfData={pdfDoc} width={isMobile ? window.innerWidth * 1 : 900} />)}
        </Box>
      </Box>
      <Box className="my-resume-mobile-footer" sx={{ display: { xs: 'block', md: 'none', lg: 'none' } }}>
        <Stack direction="row" alignItems={'center'} spacing={2}>
          <Button
            onClick={data?.link ? handleUploadResume : handleNavigation}
            variant="outlined"
            size="large"
            className="save-btn"
            fullWidth
          >{data?.link ? 'Replace' : 'Update'}</Button>
          <Button
            variant="contained"
            className="apply-now-btn"
            onClick={() => openDownloadResumeBottomSheet()}
            endIcon={<KeyboardArrowDownIcon />}
            fullWidth
          >
            Download
          </Button>
        </Stack>
      </Box>
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={isOverlayDialogOpen}
          className="forgot-password-dialog career-breaks-dialog"
        >
          <UploadResumeNavigation handleProceed={handleProceed} handleCancel={handleCancel} />
        </Drawer> :
        <Dialog
          open={isOverlayDialogOpen}
          className="action-dialog-card"
        >
          <UploadResumeNavigation handleProceed={handleProceed} handleCancel={handleCancel} />
        </Dialog>}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={isDownloadResumeDialogOpen}
          onClose={() => handleCloseDownloadResumeDialog()}
          className="download-resume-drawer mobile-bottomsheet-dialog action-dialog-card external-job-app-dialog"
        >
          <ResumeDownloaded onClose={() => handleDoItLater()} handleContinueExternalApplication={handleContinueExternalApplication} isSubmitting={isSubmitting} />
        </Drawer>
      ) : (
        <Dialog
          open={isDownloadResumeDialogOpen}
          onClose={() => handleCloseDownloadResumeDialog()}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="action-dialog-card"
        >
          <DialogContent>
            <ResumeDownloaded onClose={() => handleDoItLater()} handleContinueExternalApplication={handleContinueExternalApplication} isSubmitting={isSubmitting} />
          </DialogContent>
        </Dialog>
      )}
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={isRemoveCardOpen}
          onClose={handleRemoveCard}
          className="forgot-password-dialog otp-dialog-wrapper"
        >
          <RemoveCard onClose={handleRemoveCard} onRemove={handleDelete} title={titleData} section={'Resume'} />

        </Drawer> :
        <Dialog
          open={isRemoveCardOpen}
          onClose={handleRemoveCard}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="otp-dialog-wrapper"
        >
          <RemoveCard onClose={handleRemoveCard} onRemove={handleDelete} title={titleData} section={'Resume'} />
        </Dialog>}
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={withoutUpdateDialog}
          onClose={handleCloseLeaveWihoutUpdatingDialog}
          className="forgot-password-dialog career-breaks-dialog"
          disableAutoFocus
        >
          <LeaveWithoutCreate onClose={handleCloseLeaveWihoutUpdatingDialog} isDuplicating={true} duplicating={true} handleProcessClicked={handleProcessClicked} resumeName={data?.title} />
        </Drawer> :
        <Dialog
          open={withoutUpdateDialog}
          onClose={handleCloseLeaveWihoutUpdatingDialog}
          className="action-dialog-card"
        >
          <LeaveWithoutCreate onClose={handleCloseLeaveWihoutUpdatingDialog} isDuplicating={true} duplicating={true}
            handleProcessClicked={handleProcessClicked} resumeName={data?.title} />
        </Dialog>}

      {isMobile &&
        <Drawer
          anchor="bottom"
          open={isDownloadResumeBottomSheetOpen}
          onClose={closeDownloadResumeBottomSheet}
          className="download-resume-drawer mobile-bottomsheet-dialog action-dialog-card external-job-app-dialog"
        >
          <ResumeDownload handleDownloadResumePdf={handleDownloadResumePdf} loading={loading} data={data} handleDownloadResumeDocs={handleDownloadResumeDocs} />
        </Drawer>}
    </Box>
  );
};

export default MyResumeDetails;